import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gamesApi, waitingListApi, blindsApi, seatsApi, dealerHubApi } from '../services/api';
import vipIcon from '../images/vip_icon2.png';
import appLogo from '../images/american_pub_poker.jpg';
import VoiceSettings from '../components/VoiceSettings';
import './DealerGameControl.css';

function DealerGameControl({ username, userId, userLocation, game, onBackToGames, onLogout }) {
  const navigate = useNavigate();
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentBlindLevel, setCurrentBlindLevel] = useState(1);
  const [blindLevels, setBlindLevels] = useState([]);
  const [currentBlind, setCurrentBlind] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isBreak, setIsBreak] = useState(false);
  const [tables, setTables] = useState([]);
  const [activeTableIndex, setActiveTableIndex] = useState(0);
  const [waitingList, setWaitingList] = useState([]);
  const [gameEnded, setGameEnded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Paid places: players removed in order (most recent removal first = best finish)
  const [paidPlaces, setPaidPlaces] = useState([]);

  // Game completion
  const [gameCompleted, setGameCompleted] = useState(false);

  // Move mode: single tap selects a player, then tap destination seat
  const [movingPlayer, setMovingPlayer] = useState(null);

  // Double-tap removal
  const lastTapRef = useRef({ playerId: null, time: 0 });
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(null);

  // ===== Dealer hub upgrades =====
  const [handNotesModal, setHandNotesModal] = useState(false);
  const [handNotes, setHandNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [breakModal, setBreakModal] = useState(false);
  const [breakMinutes, setBreakMinutes] = useState(10);
  const [floorCallSent, setFloorCallSent] = useState(false);
  const [incidentModal, setIncidentModal] = useState(false);
  const [incDesc, setIncDesc] = useState('');
  const [incSeverity, setIncSeverity] = useState('low');

  const loadHandNotes = useCallback(async () => {
    try { const r = await dealerHubApi.getHandNotes(game.id); setHandNotes(r.data?.notes || []); }
    catch {}
  }, [game?.id]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await dealerHubApi.addHandNote(game.id, userId, newNote, null, null);
      setNewNote('');
      loadHandNotes();
    } catch (e) { showToast('error', e.message); }
  };

  const startBreak = async () => {
    setBreakModal(false);
    setIsPaused(true);
    showToast('success', `Break: ${breakMinutes} min`);
    setTimeout(() => { setIsPaused(false); showToast('success', 'Break over'); }, breakMinutes * 60 * 1000);
  };

  const sendFloorCall = async () => {
    try {
      await dealerHubApi.reportIncident(userId, game.id, 'floor_call', 'Floor call requested', 'medium');
      setFloorCallSent(true);
      showToast('success', '🚨 Floor called');
      setTimeout(() => setFloorCallSent(false), 5000);
    } catch (e) { showToast('error', e.message); }
  };

  const submitIncident = async () => {
    try {
      await dealerHubApi.reportIncident(userId, game.id, 'incident', incDesc, incSeverity);
      setIncidentModal(false); setIncDesc(''); setIncSeverity('low');
      showToast('success', 'Incident logged');
    } catch (e) { showToast('error', e.message); }
  };

  // Function to play alarm sound
  const playAlarm = useCallback(() => {
    try {
      const audio = new Audio(require('../alarms/alarm-1.mp3'));
      audio.play();
    } catch (err) {
      console.log('Audio playback failed:', err);
    }
  }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  // Handle advancing to next level
  const handleAdvanceLevel = useCallback(async () => {
    try {
      const response = await blindsApi.advanceLevel(game.id, userId);
      if (response.success) {
        setCurrentBlindLevel(response.data.timer.currentLevel);
        setTimeRemaining(response.data.timer.timeRemaining);
        setCurrentBlind(response.data.currentBlind);
        setIsBreak(response.data.currentBlind?.isBreak || false);
        // Voice + sound announcement
        try {
          const { announceLevelChange } = await import('../utils/blindTimerVoice');
          announceLevelChange({
            smallBlind: response.data.currentBlind?.smallBlind,
            bigBlind: response.data.currentBlind?.bigBlind,
            ante: response.data.currentBlind?.ante,
            isBreak: response.data.currentBlind?.isBreak,
          });
        } catch (e) { /* voice not critical */ }
      }
    } catch (err) {
      console.log('No more blind levels or error:', err);
    }
  }, [game?.id, userId]);

  // Fetch seating data
  const fetchSeating = useCallback(async () => {
    if (!game?.id) return;
    try {
      const seatingResponse = await gamesApi.getGameSeating(game.id);
      if (seatingResponse.success) {
        setTables(seatingResponse.data.tables || []);
      }
    } catch (err) {
      console.error('Error fetching seating:', err);
    }
  }, [game?.id]);

  // Fetch all data on mount
  useEffect(() => {
    if (!game?.id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        await fetchSeating();

        // Fetch existing game results (for page refresh persistence)
        try {
          const resultsResponse = await gamesApi.getGameResults(game.id);
          if (resultsResponse.success && resultsResponse.data.results.length > 0) {
            const existing = resultsResponse.data.results
              .sort((a, b) => a.finishPosition - b.finishPosition)
              .map(r => ({
                id: r.playerId,
                name: r.playerName,
                finishPosition: r.finishPosition,
                pointsAwarded: r.pointsAwarded,
              }));
            setPaidPlaces(existing);
          }
          // Check if game is already completed
          if (resultsResponse.data.status === 'completed') {
            setGameCompleted(true);
          }
        } catch (e) {
          console.log('Could not fetch game results:', e);
        }

        // Fetch waiting list
        try {
          const waitingResponse = await waitingListApi.getWaitingList(game.id);
          if (waitingResponse.success) {
            setWaitingList(waitingResponse.data.waitingList.map((item) => ({
              id: item.playerId,
              name: item.playerName,
              position: item.position,
            })));
          }
        } catch (e) { /* ignore */ }

        // Fetch blind timer status
        try {
          const blindResponse = await blindsApi.getBlindTimer(game.id);
          if (blindResponse.success) {
            const { timer, currentBlind: blind, blindStructure } = blindResponse.data;
            if (blindStructure?.levels) setBlindLevels(blindStructure.levels);
            if (timer) {
              setGameStarted(timer.timerStatus === 'running' || timer.timerStatus === 'paused');
              setCurrentBlindLevel(timer.currentLevel || 1);
              setTimeRemaining(timer.timeRemaining || 0);
              setIsPaused(timer.timerStatus === 'paused');
              if (blind) {
                setCurrentBlind(blind);
                setIsBreak(blind.isBreak || false);
              }
            }
          }
        } catch (e) {
          console.error('Error fetching blind timer:', e);
        }
      } catch (err) {
        console.error('Error fetching game data:', err);
        setError(err.message || 'Failed to load game data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [game?.id, fetchSeating]);

  // Poll seating every 10 seconds
  useEffect(() => {
    if (!game?.id) return;
    const interval = setInterval(fetchSeating, 10000);
    return () => clearInterval(interval);
  }, [game?.id, fetchSeating]);

  // Timer countdown effect
  useEffect(() => {
    if (!gameStarted || isPaused || gameEnded) return;
    const warningState = { _fired: {} };
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        // Multi-warning checks (5min, 1min, 30sec)
        try {
          import('../utils/blindTimerVoice').then(({ checkMultiWarning, getVoiceSettings }) => {
            const settings = getVoiceSettings();
            if (settings.enabled) {
              warningState._fired = checkMultiWarning(prev, { ...settings, _fired: warningState._fired });
            }
          });
        } catch (e) { /* voice not critical */ }

        if (prev <= 1) {
          playAlarm();
          handleAdvanceLevel();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameStarted, isPaused, gameEnded, playAlarm, handleAdvanceLevel]);

  const handleStartGame = async () => {
    try {
      const response = await blindsApi.startTimer(game.id, userId);
      if (response.success) {
        setGameStarted(true);
        setCurrentBlindLevel(response.data.timer.currentLevel);
        setTimeRemaining(response.data.timer.timeRemaining);
        setCurrentBlind(response.data.currentBlind);
        setIsBreak(response.data.currentBlind?.isBreak || false);
      }
    } catch (err) {
      console.error('Error starting game:', err);
      setError(err.message || 'Failed to start game');
    }
  };

  const handlePauseResume = async () => {
    try {
      if (isPaused) {
        const response = await blindsApi.resumeTimer(game.id, userId);
        if (response.success) setIsPaused(false);
      } else {
        const response = await blindsApi.pauseTimer(game.id, userId);
        if (response.success) {
          setIsPaused(true);
          setTimeRemaining(response.data.timer.timeRemaining);
        }
      }
    } catch (err) {
      console.error('Error pausing/resuming:', err);
    }
  };

  const handleEndGame = async () => {
    try {
      const response = await blindsApi.stopTimer(game.id, userId);
      if (response.success) {
        setGameEnded(true);
        setGameStarted(false);
      }
    } catch (err) {
      console.error('Error ending game:', err);
      setGameEnded(true);
      setGameStarted(false);
    }
  };

  // Count total seated players across all tables
  const getTotalSeatedCount = () => {
    let count = 0;
    tables.forEach(table => {
      table.seats.forEach(seat => {
        if (seat.isOccupied) count++;
      });
    });
    return count;
  };

  // --- Seat tap handler ---
  const handleSeatTap = (tableId, seatNumber) => {
    const table = tables.find(t => t.tableId === tableId);
    const seat = table?.seats?.find(s => s.seatNumber === seatNumber);
    const isOccupied = seat?.isOccupied || false;

    // If in move mode, this tap is the destination
    if (movingPlayer) {
      if (movingPlayer.tableId === tableId && movingPlayer.seatNumber === seatNumber) {
        setMovingPlayer(null);
        return;
      }
      handleMovePlayer(tableId, seatNumber);
      return;
    }

    // Normal tap on occupied seat
    if (isOccupied) {
      const now = Date.now();
      const last = lastTapRef.current;

      // Check for double-tap (within 400ms on same player)
      if (last.playerId === seat.playerId && now - last.time < 400) {
        lastTapRef.current = { playerId: null, time: 0 };
        setShowRemoveConfirm({
          playerId: seat.playerId,
          playerName: seat.playerName,
          tableId,
          seatNumber,
        });
        return;
      }

      // Single tap → start move mode
      lastTapRef.current = { playerId: seat.playerId, time: now };

      setTimeout(() => {
        if (lastTapRef.current.playerId === seat.playerId && lastTapRef.current.time === now) {
          setMovingPlayer({
            playerId: seat.playerId,
            playerName: seat.playerName,
            tableId,
            seatNumber,
            isVip: seat.isVip,
          });
        }
      }, 420);
    }
  };

  const handleMovePlayer = async (destTableId, destSeatNumber) => {
    if (!movingPlayer) return;
    try {
      const res = await seatsApi.dealerSwapSeat(
        game.id,
        movingPlayer.playerId,
        destTableId,
        destSeatNumber
      );
      if (res.success !== false) {
        showToast('success', `Moved ${formatPlayerName(movingPlayer.playerName)} to Seat ${destSeatNumber}`);
        fetchSeating();
      }
    } catch (err) {
      showToast('error', err.message || 'Failed to move player');
    } finally {
      setMovingPlayer(null);
    }
  };

  const confirmRemovePlayer = async () => {
    if (!showRemoveConfirm) return;
    try {
      // Finishing position = number of currently seated players
      const seatedCount = getTotalSeatedCount();
      const finishPosition = seatedCount;

      await seatsApi.removeSeat(game.id, showRemoveConfirm.playerId);

      // Record elimination in game_results
      try {
        await gamesApi.recordElimination(game.id, showRemoveConfirm.playerId, finishPosition);
      } catch (e) {
        console.log('Could not record elimination:', e);
      }

      // Add to paid places list
      setPaidPlaces(prev => [...prev, {
        id: showRemoveConfirm.playerId,
        name: showRemoveConfirm.playerName,
        finishPosition,
      }].sort((a, b) => a.finishPosition - b.finishPosition));

      showToast('success', `Removed ${formatPlayerName(showRemoveConfirm.playerName)} — ${getOrdinal(finishPosition)} place`);
      fetchSeating();
    } catch (err) {
      showToast('error', err.message || 'Failed to remove player');
    } finally {
      setShowRemoveConfirm(null);
    }
  };

  // Double-tap on paid place → return player to table
  const paidPlaceTapRef = useRef({ playerId: null, time: 0 });

  const handlePaidPlaceTap = (player) => {
    const now = Date.now();
    const last = paidPlaceTapRef.current;

    if (last.playerId === player.id && now - last.time < 400) {
      paidPlaceTapRef.current = { playerId: null, time: 0 };
      returnPlayerToTable(player);
      return;
    }

    paidPlaceTapRef.current = { playerId: player.id, time: now };
  };

  const returnPlayerToTable = async (player) => {
    try {
      const res = await seatsApi.dealerSeatPlayer(game.id, player.id);
      if (res.success !== false) {
        setPaidPlaces(prev => prev.filter(p => p.id !== player.id));
        showToast('success', `${formatPlayerName(player.name)} returned to table`);
        fetchSeating();
      }
    } catch (err) {
      showToast('error', err.message || 'Failed to return player to table');
    }
  };

  const handleCompleteGame = async () => {
    try {
      await gamesApi.completeGame(game.id);
      setGameCompleted(true);
      showToast('success', 'Game marked as completed');
    } catch (err) {
      showToast('error', err.message || 'Failed to complete game');
    }
  };

  const getOrdinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatGameTime = (isoString, timeZone) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const options = { hour: 'numeric', minute: '2-digit', hour12: true };
    if (timeZone) options.timeZone = timeZone;
    return date.toLocaleTimeString('en-US', options);
  };

  const formatPlayerName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
  };

  // Seat position calculator
  const getSeatPosition = (seatNumber, maxSeats) => {
    const totalSeats = maxSeats || 10;
    const seatIndex = seatNumber - 1;
    const totalSegments = totalSeats + 1;
    const angleStep = 360 / totalSegments;
    const startAngle = -90 + angleStep;
    const angleDeg = startAngle + (seatIndex * angleStep);
    const angleRad = (angleDeg * Math.PI) / 180;
    const radiusX = 46;
    const radiusY = 38;
    const x = 50 + radiusX * Math.cos(angleRad);
    const y = 50 + radiusY * Math.sin(angleRad);
    return { left: `${x}%`, top: `${y}%` };
  };

  // --- Loading ---
  if (loading) {
    return (
      <div className="dc-fullscreen">
        <div className="dc-loading">
          <div className="dc-spinner">♠</div>
          <p>Loading game data...</p>
        </div>
      </div>
    );
  }

  // --- Error ---
  if (error) {
    return (
      <div className="dc-fullscreen">
        <div className="dc-loading">
          <p className="dc-error-msg">{error}</p>
          <button className="dc-btn-primary" onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // --- Game Completed ---
  if (gameCompleted) {
    return (
      <div className="dc-fullscreen">
        <div className="dc-topbar">
          <div className="dc-topbar-left">
            <h2>Dealer Admin</h2>
            <span className="dc-topbar-divider">|</span>
            <span className="dc-topbar-venue">Game Completed</span>
          </div>
          <button className="dc-logout-btn" onClick={onLogout}>Logout</button>
        </div>
        <div className="dc-ended-body">
          <h2>🏆 Game Complete</h2>
          {paidPlaces.length > 0 ? (
            <>
              <p>Final Results</p>
              <div className="dc-final-results">
                {paidPlaces
                  .sort((a, b) => a.finishPosition - b.finishPosition)
                  .map((player) => (
                  <div key={player.id} className={`dc-final-row ${player.finishPosition <= 3 ? 'top-three' : ''}`}>
                    <span className="dc-final-place">
                      {player.finishPosition === 1 ? '🥇' : player.finishPosition === 2 ? '🥈' : player.finishPosition === 3 ? '🥉' : getOrdinal(player.finishPosition)}
                    </span>
                    <span className="dc-final-name">{player.name}</span>
                    {player.pointsAwarded > 0 && (
                      <span className="dc-final-points">{player.pointsAwarded} pts</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p>No results recorded</p>
          )}
          <button className="dc-btn-primary dc-finish-btn" onClick={() => { navigate('/dealer/games'); onBackToGames(); }}>
            Return to Games
          </button>
        </div>
      </div>
    );
  }

  // --- Main Game View ---
  const activeTable = tables[activeTableIndex] || null;
  const totalSeats = activeTable?.maxPlayers || 9;
  const occupiedCount = activeTable ? activeTable.seats.filter(s => s.isOccupied).length : 0;
  const venueName = game.venue?.name || 'Poker Game';
  const gameTime = formatGameTime(game.startTime, game.leagueTimezone) || '';

  return (
    <div className="dc-fullscreen">
      {/* Top bar */}
      <div className="dc-topbar">
        <div className="dc-topbar-left">
          <button className="dc-back-btn" onClick={() => navigate('/dealer/games')}>
            ← Back
          </button>
          <h2>{venueName}</h2>
          {gameTime && <span className="dc-topbar-time">{gameTime}</span>}
          <span className="dc-player-badge">{occupiedCount}/{totalSeats} Players</span>
          {username && (
            <>
              <span className="dc-topbar-divider">|</span>
              <span className="dc-topbar-dealer">Dealer: {username}</span>
            </>
          )}
        </div>
        <div className="dc-topbar-right">
          <button className="dc-logout-btn" onClick={() => { setHandNotesModal(true); loadHandNotes(); }} title="Hand notes">📝</button>
          <button className="dc-logout-btn" onClick={() => setBreakModal(true)} title="Break">⏸️</button>
          <button className="dc-logout-btn" onClick={sendFloorCall} title="Call floor" style={{ background: floorCallSent ? '#dc2626' : undefined }}>🚨</button>
          <button className="dc-logout-btn" onClick={() => setIncidentModal(true)} title="Report incident">⚠️</button>
          <button className="dc-logout-btn" onClick={() => setShowVoiceSettings(true)} title="Voice settings">🔊</button>
          <button className="dc-complete-btn" onClick={handleCompleteGame}>
            ✓ Completed
          </button>
          <button className="dc-logout-btn" onClick={onLogout}>Logout</button>
        </div>
      </div>
      {showVoiceSettings && <VoiceSettings onClose={() => setShowVoiceSettings(false)} />}

      <div className="dc-body">
        <div className="dc-sub-bar">
          <img src={appLogo} alt="American Pub Poker" className="dc-body-logo" />
          {tables.length > 1 && (
            <div className="dc-table-tabs">
              {tables.map((table, idx) => (
                <button
                  key={table.tableId}
                  className={`dc-table-tab ${idx === activeTableIndex ? 'active' : ''}`}
                  onClick={() => setActiveTableIndex(idx)}
                >
                  Table {table.tableNumber}
                  {' '}({table.seats.filter(s => s.isOccupied).length}/{table.maxPlayers || totalSeats})
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Poker table + waiting list row */}
        <div className="dc-table-row">
        <div className="dc-table-area">
          <div className="dc-poker-table">
            <div className="dc-table-felt">
              <div className="dc-table-center">
                {!gameStarted ? (
                  <div className="dc-center-prestart">
                    <div className="dc-center-venue">{venueName}</div>
                    <div className="dc-center-info">
                      {blindLevels.length > 0 && (
                        <span className="dc-blind-preview">
                          Blinds: ${blindLevels[0]?.smallBlind}/${blindLevels[0]?.bigBlind}
                        </span>
                      )}
                    </div>
                    <button className="dc-start-btn" onClick={handleStartGame}>
                      ▶ Start Game
                    </button>
                  </div>
                ) : isBreak ? (
                  <div className="dc-center-blinds">
                    <div className="dc-break-label">☕ BREAK</div>
                    <div className={`dc-timer ${isPaused ? 'paused' : ''}`}>{formatTime(timeRemaining)}</div>
                    <div className="dc-center-controls">
                      <button className="dc-ctrl-btn" onClick={handlePauseResume}>
                        {isPaused ? '▶ Resume' : '⏸ Pause'}
                      </button>
                      <button className="dc-ctrl-btn end" onClick={handleEndGame}>
                        ⏹ End
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="dc-center-blinds">
                    <div className="dc-blind-label">Level {currentBlindLevel}</div>
                    <div className="dc-blind-values">
                      ${currentBlind?.smallBlind || 0} / ${currentBlind?.bigBlind || 0}
                    </div>
                    {currentBlind?.ante > 0 && (
                      <div className="dc-ante">Ante: ${currentBlind.ante}</div>
                    )}
                    <div className={`dc-timer ${isPaused ? 'paused' : ''}`}>{formatTime(timeRemaining)}</div>
                    <div className="dc-center-controls">
                      <button className="dc-ctrl-btn" onClick={handlePauseResume}>
                        {isPaused ? '▶ Resume' : '⏸ Pause'}
                      </button>
                      <button className="dc-ctrl-btn end" onClick={handleEndGame}>
                        ⏹ End
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Seats around table */}
            {activeTable && [...Array(totalSeats)].map((_, index) => {
              const seatNumber = index + 1;
              const seat = activeTable.seats.find(s => s.seatNumber === seatNumber);
              const isOccupied = seat?.isOccupied || false;
              const position = getSeatPosition(seatNumber, totalSeats);

              const isMovingSource = movingPlayer && movingPlayer.tableId === activeTable.tableId && movingPlayer.seatNumber === seatNumber;
              const isMoveTarget = movingPlayer && !isMovingSource;

              let seatClasses = `dc-seat ${isOccupied ? 'occupied' : 'empty'}`;
              if (isMovingSource) seatClasses += ' moving-source';
              if (isMoveTarget) seatClasses += ' move-target';

              return (
                <div
                  key={seatNumber}
                  className={seatClasses}
                  style={position}
                  onClick={() => handleSeatTap(activeTable.tableId, seatNumber)}
                >
                  {isOccupied ? (
                    <>
                      {seat.isVip && (
                        <img src={vipIcon} alt="VIP" className="dc-vip-icon" />
                      )}
                      <div className="dc-player-avatar">👤</div>
                      <div className="dc-player-name">{formatPlayerName(seat.playerName)}</div>
                    </>
                  ) : (
                    <>
                      <div className="dc-empty-icon">{isMoveTarget ? '⬇' : '+'}</div>
                      <div className="dc-seat-label">Seat {seatNumber}</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Waiting list — right side panel */}
        <div className="dc-waiting-area">
          <div className="dc-waiting-label">Waiting List</div>
          <div className="dc-waiting-list">
            {waitingList.length > 0 ? waitingList.map((p) => (
              <span
                key={p.id}
                className="dc-waiting-chip"
                style={{ cursor: 'pointer' }}
                title="Tap to seat from waiting list"
                onClick={async () => {
                  try {
                    await seatsApi.dealerSeatPlayer(game.id, p.id);
                    fetchSeating();
                  } catch (e) {
                    showToast('error', e.message || 'Failed to seat player');
                  }
                }}
              >➕ {formatPlayerName(p.name)}</span>
            )) : (
              <span className="dc-waiting-empty">No players waiting</span>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Paid Places Panel — fixed bottom bar */}
      {paidPlaces.length > 0 && (
        <div className="dc-paid-places">
          <div className="dc-paid-label">Paid Places</div>
          <div className="dc-paid-list">
            {paidPlaces
              .sort((a, b) => a.finishPosition - b.finishPosition)
              .map((player) => (
              <div key={player.id} className="dc-paid-item" onClick={() => handlePaidPlaceTap(player)}>
                <span className="dc-paid-position">
                  {player.finishPosition === 1 ? '🥇' : player.finishPosition === 2 ? '🥈' : player.finishPosition === 3 ? '🥉' : getOrdinal(player.finishPosition)}
                </span>
                <span className="dc-paid-name">{formatPlayerName(player.name)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Move mode banner */}
      {movingPlayer && (
        <div className="dc-move-banner">
          <span className="dc-move-text">
            Moving <strong>{formatPlayerName(movingPlayer.playerName)}</strong> — Tap a seat
          </span>
          <button className="dc-move-cancel" onClick={() => setMovingPlayer(null)}>
            Cancel
          </button>
        </div>
      )}

      {/* Remove confirmation modal */}
      {showRemoveConfirm && (
        <div className="dc-modal-overlay" onClick={() => setShowRemoveConfirm(null)}>
          <div className="dc-modal" onClick={e => e.stopPropagation()}>
            <h3>Remove Player?</h3>
            <p>Remove <strong>{formatPlayerName(showRemoveConfirm.playerName)}</strong> from Seat {showRemoveConfirm.seatNumber}?</p>
            <p className="dc-modal-note dc-modal-position">
              Finishes in {getOrdinal(getTotalSeatedCount())} place
            </p>
            {waitingList.length > 0 && (
              <p className="dc-modal-note">
                {formatPlayerName(waitingList[0].name)} will be added from the waiting list.
              </p>
            )}
            <div className="dc-modal-actions">
              <button className="dc-btn-secondary" onClick={() => setShowRemoveConfirm(null)}>Cancel</button>
              <button className="dc-btn-danger" onClick={confirmRemovePlayer}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== Hand Notes Modal ========== */}
      {handNotesModal && (
        <div className="dc-modal-overlay" onClick={() => setHandNotesModal(false)}>
          <div className="dc-modal" style={{ maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3>📝 Hand Notes</h3>
            <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
              {handNotes.length === 0 ? <p style={{ color: '#888' }}>No notes yet</p> :
                handNotes.map(n => (
                  <div key={n.id} style={{ padding: 10, background: '#1a2a3e', borderRadius: 8, marginBottom: 6 }}>
                    <div>{n.note}</div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                      {new Date(n.created_at).toLocaleTimeString()}{n.first_name ? ` · ${n.first_name} ${n.last_name}` : ''}
                    </div>
                  </div>
                ))}
            </div>
            <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a hand note..." rows={3}
              style={{ width: '100%', padding: 10, borderRadius: 8, background: '#0d1929', color: '#fff', border: '1px solid #334' }} />
            <div className="dc-modal-actions">
              <button className="dc-btn-secondary" onClick={() => setHandNotesModal(false)}>Close</button>
              <button className="dc-btn-primary" onClick={addNote} disabled={!newNote.trim()}>Add Note</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== Break Modal ========== */}
      {breakModal && (
        <div className="dc-modal-overlay" onClick={() => setBreakModal(false)}>
          <div className="dc-modal" onClick={e => e.stopPropagation()}>
            <h3>⏸️ Take a Break</h3>
            <p>How many minutes?</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
              {[5, 10, 15, 20, 30].map(m => (
                <button key={m} onClick={() => setBreakMinutes(m)}
                  className={breakMinutes === m ? 'dc-btn-primary' : 'dc-btn-secondary'}>{m}m</button>
              ))}
            </div>
            <div className="dc-modal-actions">
              <button className="dc-btn-secondary" onClick={() => setBreakModal(false)}>Cancel</button>
              <button className="dc-btn-primary" onClick={startBreak}>Start Break</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== Incident Modal ========== */}
      {incidentModal && (
        <div className="dc-modal-overlay" onClick={() => setIncidentModal(false)}>
          <div className="dc-modal" onClick={e => e.stopPropagation()}>
            <h3>⚠️ Report Incident</h3>
            <label>Severity:</label>
            <select value={incSeverity} onChange={e => setIncSeverity(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 8, background: '#0d1929', color: '#fff', border: '1px solid #334', marginBottom: 10 }}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <label>Description:</label>
            <textarea value={incDesc} onChange={e => setIncDesc(e.target.value)} rows={4}
              style={{ width: '100%', padding: 10, borderRadius: 8, background: '#0d1929', color: '#fff', border: '1px solid #334' }} />
            <div className="dc-modal-actions">
              <button className="dc-btn-secondary" onClick={() => setIncidentModal(false)}>Cancel</button>
              <button className="dc-btn-primary" onClick={submitIncident} disabled={!incDesc.trim()}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div className={`dc-toast ${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

export default DealerGameControl;
